import { Action, Reducer } from 'redux';
import { AppThunkAction } from '../..';
import { ToxnotServiceClient } from '../../ToxnotServiceClient';
import { toggleCollapsedSection } from './SharedMaterialsDatabaseStateController';
import {
    SharedMaterialPreviewDetailResponse
} from "../../Models/Api/SharedMaterials/SharedMaterialPreviewDetailResponse";

// ===============================================================================

//
// State definition
//

export interface SharedMaterialPreviewState {
    isLoading: boolean;
    previewBundle: SharedMaterialPreviewDetailResponse | null;
    collapsedSections: string[];
    renderWithoutAccess: boolean;
}

// ===============================================================================

//
// Action definitions
//

const ReceiveSharedMaterialPreviewBundle = "RECEIVE_SHARED_MATERIALS_PREVIEW_BUNDLE";
type ReceiveSharedMaterialPreviewBundleType = typeof ReceiveSharedMaterialPreviewBundle;

interface ReceiveSharedMaterialPreviewBundleAction {
    type: ReceiveSharedMaterialPreviewBundleType;
    sharedMaterialsPreviewBundle: SharedMaterialPreviewDetailResponse;
}

const TogglePreviewDataSections = "TOGGLE_PREVIEW_DATA_SECTIONS";
type TogglePreviewDataSectionsType = typeof TogglePreviewDataSections;

interface TogglePreviewDataSectionsAction {
    type: TogglePreviewDataSectionsType;
    collapsedSections: string[];
}

const ToggleRenderWithoutAccess = "TOGGLE_RENDER_WITHOUT_ACCESS";

interface ToggleRenderWithoutAccessAction {
    type: typeof ToggleRenderWithoutAccess;
    renderWithoutAccess: boolean;
}

// ===============================================================================

//
// Action creators
//

type KnownAction =
    ReceiveSharedMaterialPreviewBundleAction |
    TogglePreviewDataSectionsAction |
    ToggleRenderWithoutAccessAction;

export const actionCreators = {
    loadSharedMaterialPreviewBundle: (publicInventoryId: string, publicInventoryDataId: string, inBulkPublishMode: boolean): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const toxnotServiceClient = new ToxnotServiceClient();
        toxnotServiceClient.getSharedMaterialPreview(publicInventoryId, publicInventoryDataId, inBulkPublishMode)
            .then(result => {
                if (!result.success || (result.data === null)) {
                    console.error("Failed to getSharedMaterialPreview: " + result.result);
                    return;
                }

                dispatch({ type: ReceiveSharedMaterialPreviewBundle, sharedMaterialsPreviewBundle: result.data });
            });
    },
    toggleDetailSectionCollapse: (dataSectionId: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState().sharedMaterialsPreviewState ?? null;

        if (null === state)
            return;

        const collapsedSections = state.collapsedSections;
        toggleCollapsedSection(dataSectionId, collapsedSections);        

        dispatch({ type: TogglePreviewDataSections, collapsedSections: [...collapsedSections] });
    },
    toggleRenderWithoutAccess: (renderWithoutAccess: boolean): AppThunkAction<KnownAction> => (dispatch, getState) => {
        dispatch({ type: ToggleRenderWithoutAccess, renderWithoutAccess: renderWithoutAccess });
    }
}

// ===============================================================================

//
// Reducer
//

const initialState: SharedMaterialPreviewState = {
    isLoading: true,
    previewBundle: null,
    collapsedSections: [],
    renderWithoutAccess: false
};

export const reducer: Reducer<SharedMaterialPreviewState | null> = (state: SharedMaterialPreviewState | null | undefined, incomingAction: Action): SharedMaterialPreviewState | null => {
    if (!state) {
        return initialState; // default state - no user context, it hasn't been fetched yet
    }

    switch (incomingAction.type) {
        case ReceiveSharedMaterialPreviewBundle:
            return {
                ...state,
                isLoading: false,
                previewBundle: (incomingAction as ReceiveSharedMaterialPreviewBundleAction).sharedMaterialsPreviewBundle,
            };
        case TogglePreviewDataSections:
            return {
                ...state,
                collapsedSections: (incomingAction as TogglePreviewDataSectionsAction).collapsedSections,
            };
        case ToggleRenderWithoutAccess:
            return {
                ...state,
                renderWithoutAccess: (incomingAction as ToggleRenderWithoutAccessAction).renderWithoutAccess
            };
    }

    return state;
}
